import { useState } from "react";
import { Eyebrow } from "@/components/bits";
import { useLang } from "@/i18n";

const PILLARS = [
  { num: "I", titleKey: "p1.t", copyKey: "p1.c" },
  { num: "II", titleKey: "p2.t", copyKey: "p2.c" },
  { num: "III", titleKey: "p3.t", copyKey: "p3.c" },
];

const FOUNDERS = [
  {
    key: "aditya",
    name: "Aditya Narayan Garnaik",
    roleKey: "fnd.aditya.role",
    lineKey: "fnd.aditya.line",
    img: "/founders/aditya.png",
  },
  {
    key: "vikram",
    name: "Vikram Singh Chouhan",
    roleKey: "fnd.vikram.role",
    lineKey: "fnd.vikram.line",
    img: "/founders/vikram.png",
  },
];

const FounderPhoto = ({ f }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      data-testid={`founder-photo-${f.key}`}
      className="relative aspect-[4/5] bg-[#0E1613] overflow-hidden border-b border-[#1A2A24]"
    >
      {!failed ? (
        <>
          <img
            src={f.img}
            alt={`${f.name} — portrait`}
            onError={() => setFailed(true)}
            className="w-full h-full object-cover grayscale hover:grayscale-0 transition-[filter] duration-1000"
          />
          <div className="absolute inset-0 bg-[#B8894B]/10 mix-blend-overlay pointer-events-none" />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B8894B]/70">
            Portrait slot — {f.key}
          </span>
        </div>
      )}
      <span className="absolute top-3 left-3 font-mono-label text-[9px] tracking-[0.3em] uppercase text-[#EDE7DA]/60 bg-[#0E1613]/60 px-2 py-1">
        {f.key}
      </span>
    </div>
  );
};

export const ApproachSection = () => {
  const { t } = useLang();
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="py-24 md:py-36 border-t border-[#1A2A24]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Eyebrow>{t("app.eyebrow")}</Eyebrow>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.1] max-w-3xl">
          {t("app.h1a")} <em className="italic text-[#B8894B]">{t("app.h1em")}</em>
          {t("app.h1b")}
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-[#1A2A24] border border-[#1A2A24] mt-14">
          {PILLARS.map((p) => (
            <div key={p.num} data-testid={`approach-pillar-${p.num}`} className="bg-[#0E1613] p-8 md:p-10">
              <span className="font-display italic text-[#B8894B] text-xl">{p.num}.</span>
              <h3 className="font-display text-xl md:text-2xl text-[#EDE7DA] tracking-tight mt-4">
                {t(p.titleKey)}
              </h3>
              <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                {t(p.copyKey)}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-24 md:mt-32">
          <Eyebrow>{t("fnd.eyebrow")}</Eyebrow>
          <h3 className="font-display text-2xl md:text-4xl tracking-tight text-[#EDE7DA] mt-5 leading-[1.15] max-w-2xl">
            {t("fnd.h1a")}{" "}
            <em className="italic text-[#B8894B]">{t("fnd.h1em")}</em>
            {t("fnd.h1b")}
          </h3>
          <div className="grid sm:grid-cols-2 gap-6 md:gap-10 mt-12 max-w-4xl">
            {FOUNDERS.map((f) => (
              <div
                key={f.key}
                data-testid={`founder-card-${f.key}`}
                className="border border-[#1A2A24] bg-[#131F1A] hover:border-[#B8894B]/50 transition-colors duration-500"
              >
                <FounderPhoto f={f} />
                <div className="p-7 md:p-8">
                  <p className="font-display text-xl md:text-2xl text-[#B8894B] tracking-tight">
                    {f.name}
                  </p>
                  <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#EDE7DA]/50 mt-2.5">
                    {t(f.roleKey)}
                  </p>
                  <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-4">
                    {t(f.lineKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
